/* =============================================================
   AIAssistant.jsx — Admin AI Assistant + Knowledge Base
   -------------------------------------------------------------
   Unified page for IO managers:
   - Left column  : upload documents + manage knowledge base
   - Right column : test the AI live against uploaded documents

   Tabs switch between incoming / outgoing context.
   Real document processing via process-knowledge-doc Edge Fn.
   Real answers via ai-student-assistant Edge Fn (RAG).
   ============================================================= */

import { useState, useRef, useEffect } from 'react';
import Layout             from '../components/Layout';
import WorkInProgress    from '../components/WorkInProgress';
import { useKnowledgeBase } from '../hooks/useKnowledgeBase';
import { useAuth }          from '../context/AuthContext';
import { supabase }         from '../lib/supabase';

async function openDocument(storagePath) {
  const { data, error } = await supabase.storage
    .from('knowledge-docs')
    .createSignedUrl(storagePath, 60); // 60 seconds expiry
  if (error || !data?.signedUrl) return;
  window.open(data.signedUrl, '_blank');
}

const ACCEPTED = '.pdf,.docx,.txt';

/* ─── Helpers ────────────────────────────────────────────────── */
function formatMessage(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

function fmtDate(s) {
  return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function FileTypeIcon({ fileType }) {
  const map   = { pdf: 'picture_as_pdf', docx: 'description', txt: 'article' };
  const color = { pdf: 'text-red-500',   docx: 'text-blue-500', txt: 'text-slate-500' };
  return (
    <span className={`material-symbols-outlined text-[18px] flex-shrink-0 ${color[fileType] ?? 'text-slate-400'}`}>
      {map[fileType] ?? 'insert_drive_file'}
    </span>
  );
}

function StatusChip({ status }) {
  if (status === 'ready') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  );
  if (status === 'processing') return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
      <span className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      Processing
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
      <span className="material-symbols-outlined text-[11px]">error</span>
      Error
    </span>
  );
}


/* ─── Left column: Knowledge Base panel ─────────────────────── */
function KnowledgePanel({ tab, documents, loading, uploading, uploadErr, onFileChange, onDelete, deleting, fileInputRef }) {
  const filtered = documents.filter(d => d.type === tab);

  return (
    <div className="space-y-4">
      {/* Upload zone */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <p className="text-sm text-[#464555] mb-4 leading-relaxed">
          Upload documents to train the AI for <strong>{tab === 'incoming' ? 'incoming' : 'outgoing'}</strong> students.
          It will answer questions based only on your files.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={onFileChange}
        />
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-colors
            ${uploading
              ? 'border-indigo-300 bg-indigo-50/40 cursor-wait'
              : 'border-slate-200 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 group'
            }`}
        >
          {uploading ? (
            <>
              <span className="w-8 h-8 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm font-medium text-indigo-600">Uploading & processing…</p>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-[40px] text-slate-300 group-hover:text-indigo-400 transition-colors mb-2">cloud_upload</span>
              <p className="text-sm font-medium text-slate-500 group-hover:text-indigo-600 transition-colors">Drop files here or click to upload</p>
              <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT</p>
            </>
          )}
        </div>

        {uploadErr && (
          <p className="mt-3 text-xs text-red-600 font-medium flex items-center gap-1">
            <span className="material-symbols-outlined text-[13px]">error</span>
            {uploadErr}
          </p>
        )}
      </div>

      {/* Document list */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Uploaded Documents</h3>
          {filtered.length > 0 && (
            <span className="text-xs text-slate-400">
              {filtered.filter(d => d.status === 'ready').reduce((s, d) => s + (d.chunk_count ?? 0), 0)} chunks indexed
            </span>
          )}
        </div>

        {loading ? (
          <div className="p-4 space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-6 h-6 bg-slate-100 rounded flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-slate-100 rounded w-2/3" />
                  <div className="h-2.5 bg-slate-100 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center">
            <span className="material-symbols-outlined text-slate-200 text-[36px]">folder_open</span>
            <p className="text-sm text-slate-400 mt-2">No documents yet</p>
            <p className="text-xs text-slate-400 mt-0.5">Upload your first {tab} document above.</p>
          </div>
        ) : (
          <div className="p-3 space-y-1">
            {filtered.map(doc => (
              <div key={doc.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl group hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileTypeIcon fileType={doc.file_type} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0b1c30] truncate">{doc.name}</p>
                    <p className="text-xs text-slate-400">{fmtDate(doc.created_at)}</p>
                    {doc.status === 'error' && doc.error_message && (
                      <p className="text-[10px] text-red-500 mt-0.5 truncate max-w-[200px]" title={doc.error_message}>
                        {doc.error_message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusChip status={doc.status} />
                  <button
                    onClick={() => openDocument(doc.storage_path)}
                    title="Open document"
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[15px]">open_in_new</span>
                  </button>
                  <button
                    onClick={() => onDelete(doc.id)}
                    disabled={deleting === doc.id}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                  >
                    {deleting === doc.id
                      ? <span className="w-3.5 h-3.5 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
                      : <span className="material-symbols-outlined text-[15px]">delete</span>
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ─── Right column: Test chat panel ─────────────────────────── */
function TestChatPanel({ tab, universityId }) {
  const initialMsg = {
    role: 'ai',
    text: `Hi! I'm the AI assistant for **${tab} students**. Ask me anything — I'll answer based on the documents you've uploaded.`,
  };

  const [messages, setMessages] = useState([initialMsg]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const bottomRef               = useRef(null);

  // Build history array from messages (exclude initial AI greeting)
  const buildHistory = (msgs) =>
    msgs.slice(1).map(m => ({ role: m.role, text: m.text }))

  // Reset chat when tab changes
  useEffect(() => {
    setMessages([{
      role: 'ai',
      text: `Hi! I'm the AI assistant for **${tab} students**. Ask me anything — I'll answer based on the documents you've uploaded.`,
    }]);
    setInput('');
  }, [tab]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text) => {
    const q = text.trim();
    if (!q || loading || !universityId) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setLoading(true);

    try {
      const supabaseUrl = supabase.supabaseUrl;
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token ?? '';

      // Snapshot history before adding the new user message
      const history = buildHistory(messages);

      const res = await fetch(`${supabaseUrl}/functions/v1/ai-student-assistant`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${jwt}`,
          'apikey':        supabase.supabaseKey,
        },
        body: JSON.stringify({ question: q, university_id: universityId, type: tab, history }),
      });

      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error ?? `Error ${res.status}`);

      setMessages(prev => [...prev, {
        role:      'ai',
        text:      data.answer,
        citations: data.citations ?? [],
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'ai',
        text: `Sorry, I encountered an error: ${err.message}`,
        isError: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col" style={{ height: '540px' }}>
      {/* Banner */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-indigo-50/60 rounded-t-2xl">
        <div className="w-8 h-8 rounded-xl bg-[#4343d5] flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-white text-[15px]">smart_toy</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0b1c30]">AI Test Console</p>
          <p className="text-xs text-slate-400">Testing against {tab} documents</p>
        </div>
        <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Live
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 p-5 space-y-4 overflow-y-auto">
        {messages.map((msg, i) => (
          <div key={i} className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'ai' && (
              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mb-0.5">
                <span className="material-symbols-outlined text-[13px] text-indigo-600">smart_toy</span>
              </div>
            )}
            <div className="max-w-[80%] flex flex-col gap-1.5">
              <div
                className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#4343d5] text-white rounded-br-sm'
                    : msg.isError
                      ? 'bg-red-50 text-red-700 border border-red-100 rounded-bl-sm'
                      : 'bg-slate-100 text-[#0b1c30] rounded-bl-sm'
                }`}
                dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
              />
              {msg.role === 'ai' && msg.citations?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-1">
                  {msg.citations.map((cite, ci) => (
                    <span key={ci} className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
                      <span className="material-symbols-outlined text-[10px]">description</span>
                      {cite}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex items-end gap-2 justify-start">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-[13px] text-indigo-600">smart_toy</span>
            </div>
            <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1.5">
              {[0,1,2].map(j => (
                <span key={j} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"
                      style={{ animationDelay: `${j * 150}ms` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 pb-4 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send(input)}
            placeholder={`Ask about ${tab} students…`}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition disabled:opacity-60"
          />
          <button
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            className="w-10 h-10 bg-[#4343d5] text-white rounded-xl flex items-center justify-center hover:bg-indigo-700 transition-colors disabled:opacity-40"
          >
            <span className="material-symbols-outlined text-[18px]">send</span>
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
          <span className="material-symbols-outlined text-[13px]">info</span>
          Answers are generated from your uploaded documents only
        </p>
      </div>
    </div>
  );
}


/* ─── Main component ─────────────────────────────────────────── */
export default function AIAssistant() {
  return (
    <Layout>
      <WorkInProgress
        icon="robot_2"
        title="AI Assistant"
        description="Upload your knowledge base and let students get instant AI-powered answers about their exchange process."
      />
    </Layout>
  );

  // ── Real page below — remove the return above to restore it ──
  // eslint-disable-next-line no-unreachable
  const { user } = useAuth();
  const [tab, setTab] = useState('incoming');

  const {
    documents,
    loading,
    uploadDocument,
    deleteDocument,
  } = useKnowledgeBase();

  const [uploading,  setUploading]  = useState(false);
  const [uploadErr,  setUploadErr]  = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const fileInputRef = useRef(null);

  const tabs = [
    { key: 'incoming', label: 'Incoming Students', icon: 'flight_land',    desc: 'Documents & FAQ for students arriving at ESMT' },
    { key: 'outgoing', label: 'Outgoing Students', icon: 'flight_takeoff', desc: 'Documents & FAQ for students going on exchange' },
  ];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadErr(null);
    setUploading(true);
    try {
      await uploadDocument(file, tab);
    } catch (err) {
      setUploadErr(err?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try { await deleteDocument(id); }
    catch (err) { console.error(err); }
    finally { setDeleting(null); }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">AI Assistant</h1>
        <p className="text-[#464555] mt-1">Powered by your university documents</p>
      </div>

      {/* Tab selector */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all ${
              tab === t.key
                ? 'bg-indigo-600 border-indigo-600 text-white shadow-md'
                : 'bg-white border-slate-100 text-[#0b1c30] hover:border-indigo-200 hover:bg-indigo-50/40'
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tab === t.key ? 'bg-white/20' : 'bg-slate-100'}`}>
              <span className={`material-symbols-outlined text-[22px] ${tab === t.key ? 'text-white' : 'text-slate-500'}`}>{t.icon}</span>
            </div>
            <div>
              <p className={`font-semibold text-sm ${tab === t.key ? 'text-white' : 'text-[#0b1c30]'}`}>{t.label}</p>
              <p className={`text-xs mt-0.5 ${tab === t.key ? 'text-indigo-200' : 'text-slate-400'}`}>{t.desc}</p>
            </div>
            {tab === t.key && (
              <span className="ml-auto material-symbols-outlined text-white text-[20px]">check_circle</span>
            )}
          </button>
        ))}
      </div>

      {/* Active tab label */}
      <div className="flex items-center gap-2 mb-5">
        <span className="material-symbols-outlined text-[18px] text-indigo-600">
          {tab === 'incoming' ? 'flight_land' : 'flight_takeoff'}
        </span>
        <h2 className="font-display text-base font-semibold text-[#0b1c30]">
          {tab === 'incoming' ? 'Incoming Students' : 'Outgoing Students'} — Knowledge Base & Chat
        </h2>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-2 gap-6">
        <KnowledgePanel
          tab={tab}
          documents={documents}
          loading={loading}
          uploading={uploading}
          uploadErr={uploadErr}
          onFileChange={handleFileChange}
          onDelete={handleDelete}
          deleting={deleting}
          fileInputRef={fileInputRef}
        />
        <div className="space-y-4">
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">Test your AI</h2>
          <TestChatPanel
            key={tab}
            tab={tab}
            universityId={user?.universityId ?? null}
          />
        </div>
      </div>
    </Layout>
  );
}
